import { Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, CircularProgress, Typography, Box, IconButton, Tooltip } from "@mui/material";
import { PersonRemove } from "@mui/icons-material";
import { useUpdateRecord } from "../../../api/useUpdateRecord";
import { useRecords } from "../../../api/useRecords";
import { STALE_TIME_24H } from "../../../utils/constants";
import { Avatar } from "../../../components/fieldsViews/fields/Avatar";
import { updateGMeeting } from "../sessionsOptions";
import { useStore } from "../../../store/store";
import { handleSessionSoftValidation } from "../../reserved-slots/validation/sessionSoftValidation";

type SessionStatusFieldProps = {
    value: string;
    eventId: string | number;
    setPreviewIsOpen: (value: boolean) => void;
    refetchSessions: () => void;
};

export const SessionTechnicianField = ({ value, eventId, setPreviewIsOpen, refetchSessions }: SessionStatusFieldProps) => {
    const {notifications} = useStore()
    const updateStatus = useUpdateRecord();
    const { data, status } = useRecords("technicians", "id,user_id.id,user_id.avatar,user_id.first_name,user_id.last_name", {
        extraOptions: { filter: { _or: [{ disabled: { _neq: true } }, { disabled: { _null: true } }] }, sort: "user_id.first_name" },
        fetch: { fetchOnFocus: false },
        staleTime: STALE_TIME_24H,
    });

    const handleChange = async (event: SelectChangeEvent, prevUser: any) => {
        const technicianData = { technician: event.target.value };

        try {
            const response = await updateStatus.mutateAsync({
                entity: "sessions",
                id: eventId,
                data: technicianData,
            });

            if (response.id) {
                setPreviewIsOpen(false);
                refetchSessions();

                const previousTechnicianAndIds = {
                    id: response.id,
                    meeting_id: response.meeting_id,
                    technician: prevUser,
                };

                const responseUpdate = await updateGMeeting(previousTechnicianAndIds, response);
                notifications.setNotification({
                    isOpen: true,
                    message: responseUpdate?.message,
                    severity: responseUpdate?.severity,
                });
            }
        } catch (error: any) {
            const wasHandled = await handleSessionSoftValidation(error, "sessions", async (bypassParams) => {
                const response = await updateStatus.mutateAsync({
                    entity: "sessions",
                    id: eventId,
                    data: { ...technicianData, ...bypassParams },
                });

                if (response.id) {
                    setPreviewIsOpen(false);
                    refetchSessions();

                    const previousTechnicianAndIds = {
                        id: response.id,
                        meeting_id: response.meeting_id,
                        technician: prevUser,
                    };

                    const responseUpdate = await updateGMeeting(previousTechnicianAndIds, response);
                    notifications.setNotification({
                        isOpen: true,
                        message: responseUpdate?.message,
                        severity: responseUpdate?.severity,
                    });
                }
            });

            if (!wasHandled) {
                notifications.setNotification({
                    isOpen: true,
                    message: error.response?.data?.errors?.[0]?.message ?? "Erro ao atualizar técnico",
                    severity: "error",
                });
            }
        }

    };

    const handleRemove = async () => {
        try {
            const response = await updateStatus.mutateAsync({
                entity: "sessions",
                id: eventId,
                data: { technician: null },
            });

            if (response.id) {
                refetchSessions();
                notifications.setNotification({
                    isOpen: true,
                    message: "Técnico removido com sucesso",
                    severity: "success",
                });
            }
        } catch (error: any) {
            notifications.setNotification({
                isOpen: true,
                message: error.response?.data?.errors?.[0]?.message ?? "Erro ao remover técnico",
                severity: "error",
            });
        }
    };

    if (status === "loading") return <CircularProgress />;
    if (status === "error") return <Typography>Erro ao carregar técnicos</Typography>;
    
    const prevUser = data?.items?.find(
        (item: any) => item?.id === value
      )
    
    return (
        <Box display="flex" alignItems="center" gap={1} width="100%">
            <FormControl fullWidth>
                <InputLabel>Técnico</InputLabel>
                <Select value={value || ""} onChange={(e) => handleChange(e, prevUser)} label="Técnico" sx={{ height: 56 }}>
                    {data?.items?.map((item: any) => {
                        const user = item.user_id;

                        return (
                            <MenuItem key={item.id} value={item.id}>
                                <Box display="flex" alignItems="center" gap={4}>
                                    <Avatar value={user.avatar} />
                                    <Typography>
                                        {user.first_name} {user.last_name}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>
            {value && (
                <Tooltip title="Remover técnico">
                    <IconButton onClick={handleRemove} color="error" disabled={updateStatus.isLoading} size="large">
                        <PersonRemove />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};
